import React, { useState, useEffect, useCallback, useRef } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Stack,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  InsertDriveFile as FileIcon,
  Image as PhotoIcon,
  Videocam as VideoIcon,
  Download as DownloadIcon,
  DraftsTwoTone as DraftIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  PlayCircleOutline as PlayIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as SheetIcon,
  Slideshow as SlideIcon,
} from "@mui/icons-material";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";

export interface RoomSendFileItem {
  messageId: string;
  kbn: string; // "photo" | "video" | "file" | "botphoto"
  fileName: string;
  size: string;
  time: string;
  honbuId: string;
  senderName: string;
}

interface RoomMember {
  honbuId: number;
  name: string;
}

interface RoomSendFilesContentProps {
  roomId: number;
  honbuId: number;
  palette: PaletteColor;
}

type SortKey = "time" | "kbn" | "fileName" | "size" | "senderName";
type SortDir = "asc" | "desc";
type ViewMode = "list" | "grid";

const KBN_LABELS: Record<string, string> = {
  photo: "写真",
  video: "動画",
  file: "ファイル",
  botphoto: "写真(Bot)",
};

/** サイズ文字列（バイト数）を人間が読みやすい形式に変換する */
const formatSize = (size: string): string => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || size.trim() === "") return size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/** 拡張子に応じたファイルアイコンを返す（チャットアプリ側の出し分けに準拠） */
const getFileIcon = (fileName: string, fontSize: "small" | "large" = "small") => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "pdf":
      return <PdfIcon fontSize={fontSize} sx={{ color: "#E53935" }} />;
    case "doc":
    case "docx":
      return <DocIcon fontSize={fontSize} sx={{ color: "#1E88E5" }} />;
    case "xls":
    case "xlsx":
      return <SheetIcon fontSize={fontSize} sx={{ color: "#43A047" }} />;
    case "ppt":
    case "pptx":
      return <SlideIcon fontSize={fontSize} sx={{ color: "#FB8C00" }} />;
    default:
      return <FileIcon fontSize={fontSize} sx={{ color: "#8A94A6" }} />;
  }
};

export const RoomSendFilesContent: React.FC<RoomSendFilesContentProps> = ({
  roomId,
  honbuId,
  palette,
}) => {
  const { accentColor, isDarkMode } = usePaletteColors(palette);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --- 絞り込み状態 ---
  const today = new Date().toISOString().slice(0, 10);
  const lastYear = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const [startDate, setStartDate] = useState(lastYear);
  const [endDate, setEndDate] = useState(today);
  const [showPhoto, setShowPhoto] = useState(true);
  const [showVideo, setShowVideo] = useState(true);
  const [showFile, setShowFile] = useState(true);
  const [members, setMembers] = useState<RoomMember[]>([]);
  const [selectedSenders, setSelectedSenders] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState("");

  // --- データ状態 ---
  const [items, setItems] = useState<RoomSendFileItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // --- 表示状態 ---
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [sortKey, setSortKey] = useState<SortKey>("time");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // --- プレビュー状態 ---
  const [previewItem, setPreviewItem] = useState<RoomSendFileItem | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // グリッドでサムネイル取得に失敗した messageId を記録してアイコン表示に切り替える
  const [thumbErrorIds, setThumbErrorIds] = useState<Set<string>>(new Set());

  const buildKbns = useCallback(() => {
    const parts: string[] = [];
    if (showPhoto) parts.push("photo");
    if (showVideo) parts.push("video");
    if (showFile) parts.push("file");
    return parts.join("_");
  }, [showPhoto, showVideo, showFile]);

  const fetchItems = useCallback(
    async (kbns: string, users: string, start: string, end: string) => {
      if (!kbns) {
        setItems([]);
        setSearched(true);
        return;
      }
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const params = new URLSearchParams({
          roomId: String(roomId),
          honbuId: String(honbuId),
          kbns,
          users,
          start: start.replace(/-/g, "/"),
          end: end.replace(/-/g, "/"),
        });
        const res = await fetch(`/api/files/room-send-files?${params}`);
        if (res.ok) {
          const data = await res.json();
          setItems(data.items ?? []);
        } else {
          setItems([]);
          setErrorMessage("一覧の取得に失敗しました。");
        }
      } catch {
        setItems([]);
        setErrorMessage("一覧の取得に失敗しました。");
      } finally {
        setIsLoading(false);
        setSearched(true);
      }
    },
    [roomId, honbuId],
  );

  // 初回表示: ルームメンバーを取得して全員選択の状態で自動検索する
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    (async () => {
      let allIds: string[] = [];
      try {
        const res = await fetch(`/api/files/room-members?roomId=${roomId}&honbuId=${honbuId}`);
        if (res.ok) {
          const data: RoomMember[] = await res.json();
          setMembers(data);
          allIds = data.map((m) => String(m.honbuId));
          setSelectedSenders(allIds);
        }
      } catch {
        // メンバー取得に失敗しても一覧検索は続行する（送信者フィルタなし）
      }
      await fetchItems("photo_video_file", allIds.join("_"), lastYear, today);
    })();
  }, [roomId, honbuId, fetchItems, lastYear, today]);

  const handleSearch = () => {
    fetchItems(buildKbns(), selectedSenders.join("_"), startDate, endDate);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleDownload = async (item: RoomSendFileItem) => {
    try {
      const res = await fetch(
        `/api/files/room-send-files/${item.messageId}/download?honbuId=${honbuId}`,
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.fileName || item.messageId;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download error:", e);
    }
  };

  const handleOpenPreview = (item: RoomSendFileItem) => {
    setPreviewError(false);
    setPreviewLoading(true);
    setPreviewItem(item);
  };

  const handleItemClick = (item: RoomSendFileItem) => {
    if (item.kbn === "photo" || item.kbn === "video") {
      handleOpenPreview(item);
    } else if (item.kbn === "file") {
      handleDownload(item);
    }
  };

  const thumbnailUrl = (item: RoomSendFileItem) =>
    withAuth(`/api/files/room-send-files/${item.messageId}/thumbnail?honbuId=${honbuId}`);
  const previewUrl = (item: RoomSendFileItem) =>
    withAuth(`/api/files/room-send-files/${item.messageId}/preview?honbuId=${honbuId}`);

  // ファイル名部分一致（クライアント側フィルタ）
  const filteredItems = nameFilter.trim()
    ? items.filter((i) =>
        (i.fileName ?? "").toLowerCase().includes(nameFilter.trim().toLowerCase()),
      )
    : items;

  const sortedItems = [...filteredItems].sort((a, b) => {
    let cmp: number;
    if (sortKey === "size") {
      cmp = (Number(a.size) || 0) - (Number(b.size) || 0);
    } else {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      cmp = av < bv ? -1 : av > bv ? 1 : 0;
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortLabel: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <TableSortLabel
      active={sortKey === k}
      direction={sortKey === k ? sortDir : "asc"}
      onClick={() => handleSort(k)}
    >
      {label}
    </TableSortLabel>
  );

  const kbnIcon = (kbn: string) =>
    kbn === "photo" || kbn === "botphoto" ? (
      <PhotoIcon fontSize="small" />
    ) : kbn === "video" ? (
      <VideoIcon fontSize="small" />
    ) : (
      <FileIcon fontSize="small" />
    );

  const renderEmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
      gap={1}
      sx={{ color: "text.secondary" }}
    >
      <DraftIcon sx={{ fontSize: 48, opacity: 0.3 }} />
      <Typography variant="body2">
        {errorMessage ?? "該当するファイルがありません"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 4, height: "100%", overflowY: "auto" }}>
      {/* タイトル */}
      <Box
        sx={{
          pb: 1.5,
          mb: 2,
          borderBottom: "2px dashed",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <DraftIcon sx={{ color: accentColor, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "grey.400" : "grey.800",
            }}
          >
            チャット送信ファイル
          </Typography>
        </Stack>
      </Box>

      {/* 検索フォーム */}
      <Box sx={{ pb: 2 }}>
        <Stack spacing={1}>
          {/* 行1: 期間 + 送信者 */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                期間:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ flex: 1, minWidth: 0 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Typography variant="body2" sx={{ flexShrink: 0 }}>
                〜
              </Typography>
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ flex: 1, minWidth: 0 }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ flexShrink: 0 }}>
                送信者:
              </Typography>
              <Select
                multiple
                size="small"
                value={selectedSenders}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedSenders(typeof v === "string" ? v.split(",") : v);
                }}
                renderValue={(selected) =>
                  selected.length === members.length || selected.length === 0
                    ? "全員"
                    : members
                        .filter((m) => selected.includes(String(m.honbuId)))
                        .map((m) => m.name)
                        .join(", ")
                }
                displayEmpty
                sx={{ flex: 1, minWidth: 0 }}
              >
                {members.map((m) => (
                  <MenuItem key={m.honbuId} value={String(m.honbuId)} dense>
                    <Checkbox
                      size="small"
                      checked={selectedSenders.includes(String(m.honbuId))}
                      sx={{ py: 0, "&.Mui-checked": { color: accentColor } }}
                    />
                    <ListItemText primary={m.name} />
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>

          {/* 行2: 種類 + ファイル名 + 検索ボタン */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showPhoto}
                    onChange={(e) => setShowPhoto(e.target.checked)}
                    sx={{ "&.Mui-checked": { color: accentColor } }}
                  />
                }
                label="写真"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showVideo}
                    onChange={(e) => setShowVideo(e.target.checked)}
                    sx={{ "&.Mui-checked": { color: accentColor } }}
                  />
                }
                label="動画"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showFile}
                    onChange={(e) => setShowFile(e.target.checked)}
                    sx={{ "&.Mui-checked": { color: accentColor } }}
                  />
                }
                label="ファイル"
              />
            </FormGroup>
            <TextField
              size="small"
              placeholder="ファイル名で絞り込み"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              sx={{ flex: 1, minWidth: 160 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Box
              component="button"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                border: "none",
                bgcolor: accentColor,
                color: "white",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
                opacity: isLoading ? 0.7 : 1,
                "&:hover": { filter: "brightness(0.9)" },
              }}
            >
              {isLoading ? "検索中..." : "検索"}
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v: ViewMode | null) => {
                if (v) setViewMode(v);
              }}
              sx={{
                "& .Mui-selected": { color: `${accentColor} !important` },
              }}
            >
              <ToggleButton value="list" aria-label="リスト表示">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="サムネイル表示">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Box>

      {/* 一覧本体 */}
      <Box>
        {isLoading || !searched ? (
          <Box display="flex" justifyContent="center" pt={6}>
            <CircularProgress sx={{ color: accentColor }} />
          </Box>
        ) : sortedItems.length === 0 ? (
          renderEmptyState()
        ) : viewMode === "grid" ? (
          /* --- モジュール（サムネイルグリッド）表示 --- */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 1.5,
              pb: 2,
            }}
          >
            {sortedItems.map((item) => (
              <Paper
                key={item.messageId}
                variant="outlined"
                onClick={() => handleItemClick(item)}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: item.kbn === "botphoto" ? "default" : "pointer",
                  transition: "box-shadow 0.15s",
                  "&:hover": { boxShadow: 3 },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: "1 / 1",
                    bgcolor: isDarkMode ? "grey.900" : "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(item.kbn === "photo" ||
                    item.kbn === "video" ||
                    item.kbn === "botphoto") &&
                  !thumbErrorIds.has(item.messageId) ? (
                    <>
                      <Box
                        component="img"
                        src={thumbnailUrl(item)}
                        alt={item.fileName || KBN_LABELS[item.kbn] || ""}
                        loading="lazy"
                        onError={() =>
                          setThumbErrorIds((prev) =>
                            new Set(prev).add(item.messageId),
                          )
                        }
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {item.kbn === "video" && (
                        <PlayIcon
                          sx={{
                            position: "absolute",
                            fontSize: 48,
                            color: "rgba(255,255,255,0.9)",
                            filter: "drop-shadow(0 0 4px rgba(0,0,0,0.5))",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                    </>
                  ) : item.kbn === "file" ? (
                    getFileIcon(item.fileName, "large")
                  ) : (
                    kbnIcon(item.kbn)
                  )}
                </Box>
                <Box sx={{ p: 1 }}>
                  <Tooltip title={item.fileName || KBN_LABELS[item.kbn] || ""}>
                    <Typography variant="caption" noWrap display="block">
                      {item.fileName || KBN_LABELS[item.kbn] || item.messageId}
                    </Typography>
                  </Tooltip>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Avatar
                      src={withAuth(`/api/files/members/${item.honbuId}/icon?honbuId=${honbuId}`)}
                      sx={{ width: 16, height: 16, fontSize: "0.6rem" }}
                    >
                      {item.senderName?.[0] ?? "?"}
                    </Avatar>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: "text.secondary", fontSize: "0.65rem" }}
                    >
                      {item.senderName} ・ {item.time}
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          /* --- リスト表示 --- */
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <SortLabel label="時刻" k="time" />
                    </TableCell>
                    <TableCell>
                      <SortLabel label="種類" k="kbn" />
                    </TableCell>
                    <TableCell>
                      <SortLabel label="ファイル名" k="fileName" />
                    </TableCell>
                    <TableCell>
                      <SortLabel label="サイズ" k="size" />
                    </TableCell>
                    <TableCell>
                      <SortLabel label="送信者" k="senderName" />
                    </TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((item) => (
                    <TableRow key={item.messageId} hover>
                      <TableCell
                        sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}
                      >
                        {item.time}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={kbnIcon(item.kbn)}
                          label={KBN_LABELS[item.kbn] ?? item.kbn}
                          variant="outlined"
                          sx={{ fontSize: "0.75rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        {item.kbn === "file" ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {getFileIcon(item.fileName)}
                            <Typography
                              variant="body2"
                              sx={{
                                color: accentColor,
                                cursor: "pointer",
                                textDecoration: "underline",
                                "&:hover": { opacity: 0.8 },
                              }}
                              onClick={() => handleDownload(item)}
                            >
                              {item.fileName || "(ファイル)"}
                            </Typography>
                          </Stack>
                        ) : item.kbn === "photo" || item.kbn === "video" ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: accentColor,
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": { opacity: 0.8 },
                            }}
                            onClick={() => handleOpenPreview(item)}
                          >
                            {item.kbn === "video" ? "動画を表示" : "写真を表示"}
                          </Typography>
                        ) : item.kbn === "botphoto" ? (
                          <Tooltip title="Chatisアプリから確認してください">
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.disabled",
                                fontSize: "0.8rem",
                              }}
                            >
                              写真(Bot)
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">
                            {item.fileName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                      >
                        {formatSize(item.size)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={withAuth(`/api/files/members/${item.honbuId}/icon?honbuId=${honbuId}`)}
                            sx={{ width: 24, height: 24, fontSize: "0.7rem" }}
                          >
                            {item.senderName?.[0] ?? "?"}
                          </Avatar>
                          <Typography variant="body2">
                            {item.senderName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {item.kbn === "file" && (
                          <Tooltip title="ダウンロード">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(item)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* 写真/動画プレビューオーバーレイ */}
      {previewItem && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.8)",
            zIndex: 1400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setPreviewItem(null)}
        >
          {previewLoading && !previewError && (
            <CircularProgress
              sx={{ position: "absolute", color: "white" }}
            />
          )}
          {previewError ? (
            <Typography color="white" onClick={(e) => e.stopPropagation()}>
              表示できませんでした
            </Typography>
          ) : previewItem.kbn === "video" ? (
            <video
              src={previewUrl(previewItem)}
              controls
              autoPlay
              style={{ maxWidth: "90vw", maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
              onLoadedData={() => setPreviewLoading(false)}
              onError={() => {
                setPreviewLoading(false);
                setPreviewError(true);
              }}
            />
          ) : (
            <img
              src={previewUrl(previewItem)}
              alt={previewItem.fileName || "preview"}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
              onClick={(e) => e.stopPropagation()}
              onLoad={() => setPreviewLoading(false)}
              onError={() => {
                setPreviewLoading(false);
                setPreviewError(true);
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
